import React, { useState } from "react";
import ConflictModal from "../components/shared/ConflictModal";
import SickDayConfirmModal from "../components/SickDayConfirmModal";
import "./Common.css"; // Modal base styles
import "./Uploads.css"; // Conflict modal styles
import "./TestModals.css";

/**
 * TestModals Component
 *
 * Testing page for CustomFormInput migration (DL-04FEB26-01)
 * Allows visual testing and interaction testing of migrated modals
 *
 * Access at: /test-modals
 *
 * Modals being tested:
 * 1. ConflictModal - Radio buttons for iCal conflict resolution
 * 2. SickDayConfirmModal - Checkboxes for booking selection
 *
 * Created: 2026-02-06
 */
const TestModals = () => {
    // ========================================
    // CONFLICT MODAL STATE
    // ========================================
    const [showConflictModal, setShowConflictModal] = useState(false);
    const [conflictResolution, setConflictResolution] = useState(null);

    const sampleConflicts = [
        {
            newEvent: {
                title: "Salsa Basics Workshop",
                eventStartDateTime: "2026-02-15T18:00:00Z",
            },
            conflictType: "ical_update",
            existingSource: "Facebook Import",
            lastUpdated: "2026-02-01T10:30:00Z",
        },
        {
            newEvent: {
                title: "Bachata Social Dance",
                eventStartDateTime: "2026-02-20T19:00:00Z",
            },
            conflictType: "potential_duplicate",
            existingSource: "Manual Entry",
            lastUpdated: "2026-02-05T14:20:00Z",
        },
        {
            newEvent: {
                title: "Past Event Example",
                eventStartDateTime: "2025-12-01T18:00:00Z",
            },
            conflictType: "past_event",
            existingSource: "iCal Import",
            lastUpdated: "2025-11-30T09:00:00Z",
        },
    ];

    const handleConflictResolve = resolutions => {
        if (resolutions.length === 0) {
            setConflictResolution("Cancelled - no changes made");
        } else {
            setConflictResolution(JSON.stringify(resolutions, null, 2));
        }
        setShowConflictModal(false);
    };

    // ========================================
    // SICK DAY MODAL STATE
    // ========================================
    const [showSickDayModal, setShowSickDayModal] = useState(false);
    const [sickDayResult, setSickDayResult] = useState(null);

    const sampleBookings = [
        {
            id: "booking-1",
            studentName: "Alice Johnson",
            timeSlot: "09:00 AM",
            lessonType: "Private Salsa Lesson",
            status: "confirmed",
        },
        {
            id: "booking-2",
            studentName: "Bob Martinez",
            timeSlot: "11:00 AM",
            lessonType: "Bachata Technique",
            status: "confirmed",
        },
        {
            id: "booking-3",
            studentName: "Carol Lee",
            timeSlot: "02:00 PM",
            lessonType: "Group Dance Class",
            status: "confirmed",
        },
        {
            id: "booking-4",
            studentName: "David Kim",
            timeSlot: "04:00 PM",
            lessonType: "Private Tango Lesson",
            status: "confirmed",
        },
    ];

    const handleSickDayConfirm = (message, unpaidBookingIds = [], date, paidDecisions = []) => {
        if (unpaidBookingIds.length === 0 && paidDecisions.length === 0) {
            setSickDayResult("Cancelled - no bookings selected");
        } else {
            const selectedBookings = sampleBookings.filter(b => unpaidBookingIds.includes(b.id));
            setSickDayResult(JSON.stringify({ date, selectedBookings, paidDecisions }, null, 2));
        }
        setShowSickDayModal(false);
    };

    return (
        <div className="test-modals-container">
            {/* Header */}
            <div className="test-modals-header">
                <h1>Modal Testing Page</h1>
                <p className="test-modals-subtitle">CustomFormInput Migration Testing (DL-04FEB26-01)</p>
                <p className="test-modals-description">
                    This page allows testing of modals being migrated to use CustomFormInput component. Click the
                    buttons below to open each modal and test the radio button / checkbox interactions.
                </p>
            </div>

            {/* ========================================
                SECTION 1: CONFLICT MODAL
                ======================================== */}
            <section className="test-section">
                <div className="section-header">
                    <h2>1. ConflictModal (Radio Buttons)</h2>
                    <p className="section-description">
                        Tests radio button migration for iCal conflict resolution. Displays 3 sample conflicts with
                        different resolution options.
                    </p>
                </div>

                <div className="test-controls">
                    <button onClick={() => setShowConflictModal(true)} className="test-button test-button-primary">
                        Open ConflictModal
                    </button>
                    <button onClick={() => setConflictResolution(null)} className="test-button test-button-secondary">
                        Clear Result
                    </button>
                </div>

                {conflictResolution && (
                    <div className="test-result">
                        <h3>Resolution Result:</h3>
                        <pre>{conflictResolution}</pre>
                    </div>
                )}

                <div className="test-info">
                    <h4>Testing Checklist:</h4>
                    <ul>
                        <li>✓ Radio buttons render with correct variants (turquoise, orange, purple)</li>
                        <li>✓ Only one option can be selected per conflict</li>
                        <li>✓ Default selection is "Skip (keep existing)"</li>
                        <li>✓ Third option ("Update existing event") only shows for ical_update type</li>
                        <li>✓ "Apply Decisions" captures all selections</li>
                        <li>✓ "Cancel" returns empty array</li>
                    </ul>
                </div>
            </section>

            {/* ========================================
                SECTION 2: SICK DAY MODAL
                ======================================== */}
            <section className="test-section">
                <div className="section-header">
                    <h2>2. SickDayConfirmModal (Checkboxes)</h2>
                    <p className="section-description">
                        Tests checkbox migration for booking selection during sick day cancellation. Displays 4 sample
                        bookings to select for cancellation.
                    </p>
                </div>

                <div className="test-controls">
                    <button onClick={() => setShowSickDayModal(true)} className="test-button test-button-primary">
                        Open SickDayConfirmModal
                    </button>
                    <button onClick={() => setSickDayResult(null)} className="test-button test-button-secondary">
                        Clear Result
                    </button>
                </div>

                {sickDayResult && (
                    <div className="test-result">
                        <h3>Selected Bookings:</h3>
                        <pre>{sickDayResult}</pre>
                    </div>
                )}

                <div className="test-info">
                    <h4>Testing Checklist:</h4>
                    <ul>
                        <li>✓ Checkboxes render with turquoise variant</li>
                        <li>✓ Multiple bookings can be selected</li>
                        <li>✓ Checkboxes can be toggled on/off</li>
                        <li>✓ "Confirm" returns selected booking IDs</li>
                        <li>✓ "Cancel" closes modal without changes</li>
                        <li>✓ Disabled state works (if processing)</li>
                    </ul>
                </div>
            </section>

            {/* ========================================
                SECTION 3: MIGRATION STATUS
                ======================================== */}
            <section className="test-section">
                <div className="section-header">
                    <h2>Migration Progress</h2>
                </div>

                <div className="migration-status">
                    <table className="status-table">
                        <thead>
                            <tr>
                                <th>File</th>
                                <th>Input Type</th>
                                <th>Status</th>
                                <th>Test Button</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>ConflictModal.js</td>
                                <td>Radio (3 options)</td>
                                <td className="status-pending">⏳ Pending</td>
                                <td>
                                    <button onClick={() => setShowConflictModal(true)} className="test-button-small">
                                        Test
                                    </button>
                                </td>
                            </tr>
                            <tr>
                                <td>SickDayConfirmModal.js</td>
                                <td>Checkbox (list)</td>
                                <td className="status-pending">⏳ Pending</td>
                                <td>
                                    <button onClick={() => setShowSickDayModal(true)} className="test-button-small">
                                        Test
                                    </button>
                                </td>
                            </tr>
                            <tr>
                                <td>Settings.js</td>
                                <td>Radio (groups)</td>
                                <td className="status-pending">⏳ Pending</td>
                                <td>
                                    <button disabled className="test-button-small">
                                        Coming Soon
                                    </button>
                                </td>
                            </tr>
                            <tr>
                                <td>Uploads.js</td>
                                <td>Radio (groups)</td>
                                <td className="status-pending">⏳ Pending</td>
                                <td>
                                    <button disabled className="test-button-small">
                                        Coming Soon
                                    </button>
                                </td>
                            </tr>
                            <tr>
                                <td>CheckoutForm.js</td>
                                <td>Checkbox (terms)</td>
                                <td className="status-pending">⏳ Pending</td>
                                <td>
                                    <button disabled className="test-button-small">
                                        Coming Soon
                                    </button>
                                </td>
                            </tr>
                            <tr>
                                <td>TicketWizard.js</td>
                                <td>Checkbox (multiple)</td>
                                <td className="status-pending">⏳ Pending</td>
                                <td>
                                    <button disabled className="test-button-small">
                                        Coming Soon
                                    </button>
                                </td>
                            </tr>
                            <tr>
                                <td>TicketPurchase.js</td>
                                <td>Radio (ticket types)</td>
                                <td className="status-pending">⏳ Pending</td>
                                <td>
                                    <button disabled className="test-button-small">
                                        Coming Soon
                                    </button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </section>

            {/* Render Modals */}
            {showConflictModal && <ConflictModal conflicts={sampleConflicts} onResolve={handleConflictResolve} />}

            {showSickDayModal && (
                <SickDayConfirmModal
                    isOpen={showSickDayModal}
                    onClose={() => setShowSickDayModal(false)}
                    todaysBookings={sampleBookings}
                    onConfirm={handleSickDayConfirm}
                />
            )}
        </div>
    );
};

export default TestModals;
